import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Select,
  Modal,
  Alert,
  Empty,
  Typography,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import apiHelpers from "./data";
import buildColumns from "./columns";
import BucketFormModal from "./BucketFormModal";
import { STORES, hashForLabel, labelForHash } from "../BlogsTable/stores";
import { triggerNotification } from "../../common/notify";
import { DEFAULT_STORE_LABEL, SHARED_STORE_LS_KEY } from "../common/storeDefaults";

const LS_STORE_KEY = SHARED_STORE_LS_KEY;

const STORE_OPTIONS = [
  ...STORES.map((s) => ({ value: s.hash, label: `${s.label} — ${s.name}` })),
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const validStoreHashes = STORES.map((s) => s.hash);

const readInitialFilters = (searchParams) => {
  const lsStoreLabel = localStorage.getItem(LS_STORE_KEY);
  const lsStoreHash = hashForLabel(lsStoreLabel);
  const qpStore = searchParams.get("bstore");
  let store_hash = "";
  if (qpStore === "" || qpStore === null) {
    store_hash = lsStoreHash || hashForLabel(DEFAULT_STORE_LABEL) || "";
  } else {
    store_hash = validStoreHashes.includes(qpStore) ? qpStore : "";
  }

  return {
    store_hash,
    search: searchParams.get("bsearch") || "",
    sort_by: "sort_order",
    sort_order: "ASC",
    page: Math.max(parseInt(searchParams.get("bpage"), 10) || 1, 1),
    limit: PAGE_SIZE_OPTIONS.includes(parseInt(searchParams.get("blimit"), 10))
      ? parseInt(searchParams.get("blimit"), 10)
      : 20,
  };
};

const BucketsTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => readInitialFilters(searchParams));
  const [searchInput, setSearchInput] = useState(filters.search);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [modalState, setModalState] = useState({ open: false, mode: "create", id: null });

  useEffect(() => {
    const label = labelForHash(filters.store_hash);
    if (label) localStorage.setItem(LS_STORE_KEY, label);
  }, [filters.store_hash]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("bstore", filters.store_hash);
    if (filters.search) next.set("bsearch", filters.search);
    else next.delete("bsearch");
    next.set("bpage", String(filters.page));
    next.set("blimit", String(filters.limit));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((prev) =>
        prev.search === searchInput ? prev : { ...prev, search: searchInput, page: 1 }
      );
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const fetchData = useCallback(async () => {
    setFetching(true);
    setLoadError(null);
    try {
      const { items, pagination: pg } = await apiHelpers.fetchBuckets(filters);
      setRows(items);
      setPagination(pg);
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      setLoadError(mapped);
      setRows([]);
      setPagination({ total: 0, totalPages: 0 });
      triggerNotification({
        type: "error",
        title: "Failed to load buckets",
        message: mapped.message,
      });
    } finally {
      setLoading(false);
      setFetching(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateFilter = (patch) =>
    setFilters((prev) => ({ ...prev, ...patch, page: patch.page ?? 1 }));

  const resetFilters = () => {
    setSearchInput("");
    setFilters({
      store_hash: hashForLabel(DEFAULT_STORE_LABEL) || "",
      search: "",
      sort_by: "sort_order",
      sort_order: "ASC",
      page: 1,
      limit: 20,
    });
  };

  const openCreate = () => setModalState({ open: true, mode: "create", id: null });
  const openEdit = useCallback(
    (record) => setModalState({ open: true, mode: "edit", id: record.id }),
    []
  );
  const closeModal = () => setModalState((s) => ({ ...s, open: false }));

  const handleDelete = useCallback(
    (record) => {
      Modal.confirm({
        title: "Delete bucket?",
        icon: <ExclamationCircleOutlined />,
        width: 520,
        content: (
          <div>
            <p style={{ marginTop: 0 }}>
              Delete <b>{record.displayName || record.bucketKey}</b>?
            </p>
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message="This also removes every product assignment for this bucket."
            />
          </div>
        ),
        okText: "Delete bucket and assignments",
        okButtonProps: { danger: true },
        cancelText: "Cancel",
        onOk: async () => {
          const snapshot = rows;
          setRows((prev) => prev.filter((r) => r.id !== record.id));
          try {
            await apiHelpers.deleteBucket(record.id);
            triggerNotification({
              type: "success",
              title: "Deleted",
              message: `Bucket "${record.displayName || record.bucketKey}" was deleted.`,
            });
            fetchData();
          } catch (err) {
            setRows(snapshot);
            const mapped = apiHelpers.mapError(err);
            triggerNotification({
              type: mapped.code === "REFERENCED" ? "warning" : "error",
              title:
                mapped.code === "REFERENCED" ? "Cannot delete bucket" : "Delete failed",
              message: mapped.message,
            });
          }
        },
      });
    },
    [rows, fetchData]
  );

  const columns = useMemo(
    () =>
      buildColumns({
        onEdit: openEdit,
        onDelete: handleDelete,
        page: filters.page,
        limit: filters.limit,
      }),
    [openEdit, handleDelete, filters.page, filters.limit]
  );

  const showFullPageLoader = loading && !rows.length;

  return (
    <>
      <div style={{ marginBottom: 12 }} className="TableFilterBox">
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <Space wrap>
            <Select
              value={filters.store_hash}
              onChange={(v) => updateFilter({ store_hash: v })}
              options={STORE_OPTIONS}
              style={{ width: 220 }}
              placeholder="Store"
            />
            <Input
              allowClear
              placeholder="Search key or display name"
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ width: 260 }}
            />
            <Select
              value={filters.limit}
              onChange={(v) => updateFilter({ limit: v })}
              options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }))}
              style={{ width: 120 }}
            />
            <Button icon={<ReloadOutlined />} onClick={resetFilters}>
              Reset
            </Button>
          </Space>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Bucket
          </Button>
        </Space>
      </div>

      {loadError && !fetching && (
        <Alert
          type="error"
          showIcon
          message={loadError.message}
          action={
            <Button size="small" onClick={fetchData}>
              Retry
            </Button>
          }
          style={{ marginBottom: 12 }}
        />
      )}

      <div
        className="TableBox"
        style={{
          maxHeight: 700,
          overflow: "auto",
          opacity: fetching && !showFullPageLoader ? 0.6 : 1,
          transition: "opacity 0.15s",
        }}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={showFullPageLoader}
          locale={{
            emptyText: (
              <Empty
                description={
                  <span>
                    No buckets found. Click <b>Add Bucket</b> to create one.
                  </span>
                }
              />
            ),
          }}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total: pagination.total,
            showSizeChanger: false,
            showQuickJumper: true,
            position: ["bottomLeft"],
            showTotal: (total) => (
              <Typography.Text type="secondary">
                {total} result{total === 1 ? "" : "s"} · Page {filters.page} of{" "}
                {pagination.totalPages || 1}
              </Typography.Text>
            ),
            onChange: (page) => setFilters((prev) => ({ ...prev, page })),
          }}
          bordered
          scroll={{ x: 1100, y: 500 }}
        />
      </div>

      <BucketFormModal
        open={modalState.open}
        mode={modalState.mode}
        bucketId={modalState.id}
        defaultStoreHash={filters.store_hash || hashForLabel(DEFAULT_STORE_LABEL)}
        onClose={closeModal}
        onSaved={() => {
          closeModal();
          fetchData();
        }}
      />
    </>
  );
};

export default BucketsTable;
